import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SimpleMapView } from '@/components/displays/SimpleMapView';
import { DisplayList } from '@/components/displays/DisplayList';
import { DisplayDetailDrawerTabbed } from '@/components/displays/DisplayDetailDrawerTabbed';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Navigation, Menu, X, Search, Map, List, ListFilter } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { TypesenseSearchService } from '@/lib/search/typesense/SearchService';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface Display {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  holiday_type: string;
  display_type: string;
  image_url: string;
  featured: boolean;
  upvote_count: number;
  holidays?: string[];
}

const HOLIDAY_FILTERS = [
  'Christmas',
  'Halloween',
  'Easter',
  'Fourth of July',
  'Thanksgiving',
  'Valentine\'s Day'
];

// Standardized display type categories
const DISPLAY_TYPE_FILTERS = [
  'Residential',
  'Commercial',
  'Community',
  'Walk-Through',
  'Drive-Through',
  'Haunted House'
];

// Typesense pagination limit
const TYPESENSE_MAX_PER_PAGE = 250;

export default function Displays() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get search query from URL
  const searchQuery = searchParams.get('q') || '';

  // Core state - Typesense results
  const [displays, setDisplays] = useState<Display[]>([]); // For list view (paginated)
  const [mapDisplays, setMapDisplays] = useState<Display[]>([]); // For map view (all points)
  const [totalFound, setTotalFound] = useState(0);
  const [facets, setFacets] = useState<Record<string, Record<string, number>>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMapData, setIsLoadingMapData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState<'map' | 'list' | 'split'>('split'); // Default to split view (Yelp-style)

  // Search state
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const debouncedSearchQuery = useDebouncedValue(localSearchQuery, 300);

  // Filter state
  const [selectedHolidays, setSelectedHolidays] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedDisplayTypes, setSelectedDisplayTypes] = useState<string[]>([]);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [showSynchronizedOnly, setShowSynchronizedOnly] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState<'popular' | 'trending' | 'recent' | 'featured'>('popular');

  // Selection state - replaces pagination with drawer
  const [selectedDisplayId, setSelectedDisplayId] = useState<string | null>(null);

  // Pagination state for list view
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Yelp-style: 10 items per page

  // User location
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize filters from URL params
  useEffect(() => {
    const stateParam = searchParams.get('state');
    const holidayParam = searchParams.get('holiday');
    const featuredParam = searchParams.get('featured');

    if (stateParam) setSelectedState(stateParam);
    if (holidayParam) setSelectedHolidays([holidayParam]);
    if (featuredParam === 'true') setShowFeaturedOnly(true);
  }, [searchParams]);

  // Removed auto-switch logic - list view is now the permanent default

  // Map sort option to Typesense sort_by parameter
  const getSortBy = useCallback((sortOption: string) => {
    switch (sortOption) {
      case 'popular':
        return 'upvote_count:desc';
      case 'trending':
        return 'hot_score:desc';
      case 'recent':
        return 'created_at:desc';
      case 'featured':
        return 'featured:desc,upvote_count:desc';
      default:
        return 'upvote_count:desc';
    }
  }, []);

  // Fetch all displays for map (multi-page)
  const fetchAllMapDisplays = useCallback(async () => {
    try {
      setIsLoadingMapData(true);

      // Build filters object
      const filters: Record<string, any> = {
        status: 'active'
      };

      if (selectedHolidays.length > 0) {
        filters.holidays = selectedHolidays;
      }

      if (selectedState && selectedState !== '' && selectedState !== 'all') {
        filters.state = selectedState;
      }

      if (showFeaturedOnly) {
        filters.featured = true;
      }

      if (selectedDisplayTypes.length > 0) {
        filters.display_type = selectedDisplayTypes;
      }

      if (showSynchronizedOnly) {
        filters.is_synced_to_music = true;
      }

      // Fetch multiple pages to get all map points (up to 2500)
      const maxPages = 10; // 10 pages * 250 = 2500 points max
      const allDisplays: Display[] = [];

      for (let page = 1; page <= maxPages; page++) {
        const searchParams: Record<string, any> = {
          query: debouncedSearchQuery || '*',
          page,
          per_page: TYPESENSE_MAX_PER_PAGE,
          sort_by: getSortBy(sortBy),
          filters
        };

        const result = await TypesenseSearchService.searchDisplays(searchParams);

        const transformedDisplays: Display[] = (result.hits || []).map((hit: any) => {
          const doc = hit.document;
          return {
            id: doc.id,
            name: doc.display_name || '',
            description: doc.description || '',
            latitude: doc.location ? doc.location[0] : 0,
            longitude: doc.location ? doc.location[1] : 0,
            city: doc.city || '',
            state: doc.state || '',
            holiday_type: doc.holiday_type?.[0] || '',
            display_type: doc.display_type || '',
            image_url: doc.image_url || '',
            featured: doc.featured || false,
            upvote_count: doc.upvote_count || 0,
            holidays: doc.holiday_type || []
          };
        });

        allDisplays.push(...transformedDisplays);

        // Stop if we've fetched all available results
        if (transformedDisplays.length < TYPESENSE_MAX_PER_PAGE) {
          break;
        }
      }

      console.log(`Fetched ${allDisplays.length} displays for map view`);
      setMapDisplays(allDisplays);
      setIsLoadingMapData(false);
    } catch (error) {
      console.error('Failed to fetch map displays:', error);
      setIsLoadingMapData(false);
    }
  }, [debouncedSearchQuery, selectedHolidays, selectedState, showFeaturedOnly, selectedDisplayTypes, showSynchronizedOnly, sortBy, getSortBy]);

  // Search displays for list view (single page, paginated)
  const searchDisplays = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build search parameters for list view (10 items)
      const searchParams: Record<string, any> = {
        query: debouncedSearchQuery || '*',
        page: currentPage,
        per_page: itemsPerPage, // Always 10 for list
        sort_by: getSortBy(sortBy),
      };

      // Build filters object
      const filters: Record<string, any> = {};

      if (selectedHolidays.length > 0) {
        filters.holidays = selectedHolidays;
      }

      if (selectedState && selectedState !== '' && selectedState !== 'all') {
        filters.state = selectedState;
      }

      if (showFeaturedOnly) {
        filters.featured = true;
      }

      if (selectedDisplayTypes.length > 0) {
        filters.display_type = selectedDisplayTypes;
      }

      if (showSynchronizedOnly) {
        filters.is_synced_to_music = true;
      }

      // Only show active displays to public (hides draft and archived)
      filters.status = 'active';

      // Add filters to searchParams
      searchParams.filters = filters;

      console.log('Search params:', searchParams);

      // Execute search
      const result = await TypesenseSearchService.searchDisplays(searchParams);
      console.log('Typesense returned:', result.hits?.length, 'results out of', result.found, 'total');

      // Transform results to Display format
      const transformedDisplays: Display[] = (result.hits || []).map((hit: any) => {
        const doc = hit.document;
        return {
          id: doc.id,
          name: doc.display_name || '',
          description: doc.description || '',
          latitude: doc.location ? doc.location[0] : 0,
          longitude: doc.location ? doc.location[1] : 0,
          city: doc.city || '',
          state: doc.state || '',
          holiday_type: doc.holiday_type?.[0] || '',
          display_type: doc.display_type || '',
          image_url: doc.image_url || '',
          featured: doc.featured || false,
          upvote_count: doc.upvote_count || 0,
          holidays: doc.holiday_type || []
        };
      });

      // TODO: Re-enable after syncing location data to Typesense
      // Most displays currently lack location field in Typesense, causing (0,0) coordinates
      // Need to run: npm run sync-displays
      // const validDisplays = transformedDisplays.filter(d =>
      //   d.latitude !== 0 && d.longitude !== 0
      // );
      // console.log('After coordinate filter:', validDisplays.length, 'valid displays');

      setDisplays(transformedDisplays);
      setTotalFound(result.found || 0);

      // Transform facet_counts array to object format
      const facetsObject: Record<string, Record<string, number>> = {};
      if (result.facet_counts && Array.isArray(result.facet_counts)) {
        result.facet_counts.forEach((facet: any) => {
          facetsObject[facet.field_name] = {};
          facet.counts.forEach((count: any) => {
            facetsObject[facet.field_name][count.value] = count.count;
          });
        });
      }
      setFacets(facetsObject);
    } catch (err) {
      console.error('Failed to search displays:', err);
      setError(err instanceof Error ? err.message : 'Failed to search displays');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchQuery, currentPage, viewMode, itemsPerPage, sortBy, selectedHolidays, selectedState, showFeaturedOnly, selectedDisplayTypes, showSynchronizedOnly, getSortBy]);

  // Search displays when filters, search, or page changes
  useEffect(() => {
    searchDisplays();
  }, [searchDisplays]);

  // Fetch all map displays when in split/map view or when filters change
  useEffect(() => {
    if (viewMode === 'split' || viewMode === 'map') {
      fetchAllMapDisplays();
    }
  }, [viewMode, debouncedSearchQuery, selectedHolidays, selectedState, showFeaturedOnly, sortBy, fetchAllMapDisplays]);

  // Calculate available states from Typesense facets
  const availableStates = useMemo(() => {
    if (!facets.state) return [];
    return Object.keys(facets.state).sort();
  }, [facets]);

  // Calculate holiday type counts from Typesense facets
  const holidayCounts = useMemo(() => {
    if (!facets.holiday_type) return {};
    return facets.holiday_type;
  }, [facets]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedHolidays, selectedState, showFeaturedOnly, debouncedSearchQuery, sortBy]);

  // Get user location
  const getUserLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    setIsGettingLocation(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000 // 5 minutes
        });
      });

      setUserLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
    } catch (err) {
      console.warn('Failed to get location:', err);
    } finally {
      setIsGettingLocation(false);
    }
  }, []);

  // Get location on mount
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  // Get selected display object
  const selectedDisplay = selectedDisplayId
    ? displays.find(display => display.id === selectedDisplayId)
    : null;

  // Pagination calculations for list view
  // Note: For Typesense, pagination happens server-side, so displays already contains current page
  const totalPages = Math.ceil(totalFound / itemsPerPage);
  const paginatedDisplays = displays;

  // Handle display selection from map or list
  const handleDisplaySelect = useCallback((displayOrId: Display | string | null) => {
    // Extract ID from Display object or use string directly
    const displayId = typeof displayOrId === 'object' && displayOrId !== null
      ? displayOrId.id
      : displayOrId;

    setSelectedDisplayId(displayId);

    // Close mobile sidebar when selecting a display
    if (displayId && isMobile && showSidebar) {
      setShowSidebar(false);
    }
  }, [isMobile, showSidebar]);

  // Handle closing the drawer
  const handleCloseDrawer = useCallback(() => {
    setSelectedDisplayId(null);
  }, []);

  // Handle navigation to display detail page
  const handleNavigateToDetails = useCallback((displayOrId: Display | string) => {
    const displayId = typeof displayOrId === 'object' ? displayOrId.id : displayOrId;
    navigate(`/display/${displayId}`);
  }, [navigate]);

  // Filter handlers
  const toggleHolidayFilter = useCallback((holiday: string) => {
    setSelectedHolidays(prev =>
      prev.includes(holiday)
        ? prev.filter(h => h !== holiday)
        : [...prev, holiday]
    );
  }, []);

  // Toggle display type filter
  const toggleDisplayTypeFilter = useCallback((type: string) => {
    setSelectedDisplayTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedHolidays([]);
    setSelectedState('all');
    setShowFeaturedOnly(false);
    setSelectedDisplayTypes([]);
    setShowSynchronizedOnly(false);
    setLocalSearchQuery('');
    setSearchParams({});
  }, [setSearchParams]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      setSearchParams({ q: localSearchQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  // Sync local search query with URL query
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Sidebar content
  const SidebarContent = () => (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Filters</h2>
        {(selectedHolidays.length > 0 || (selectedState && selectedState !== 'all') || showFeaturedOnly || searchQuery) && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            Clear All
          </Button>
        )}
      </div>

      {/* Search Input */}
      <div>
        <h3 className="font-medium mb-3">Search</h3>
        <form onSubmit={handleSearchSubmit}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              type="text"
              placeholder="Search displays..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </form>
        {searchQuery && (
          <p className="text-xs text-muted-foreground mt-2">
            Searching for: "{searchQuery}"
          </p>
        )}
      </div>

      {/* State Filter */}
      <div>
        <h3 className="font-medium mb-3">State</h3>
        <Select value={selectedState} onValueChange={setSelectedState}>
          <SelectTrigger>
            <SelectValue placeholder="All States" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {availableStates.map(state => (
              <SelectItem key={state} value={state}>
                {state}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Featured Filter */}
      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showFeaturedOnly}
            onChange={(e) => setShowFeaturedOnly(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm">Featured displays only</span>
        </label>
      </div>

      {/* Holiday Filters */}
      <div>
        <h3 className="font-medium mb-3">Holiday Type</h3>
        <div className="space-y-2">
          {HOLIDAY_FILTERS.map(holiday => {
            const count = holidayCounts[holiday] || 0;
            return (
              <label key={holiday} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedHolidays.includes(holiday)}
                  onChange={() => toggleHolidayFilter(holiday)}
                  className="rounded border-border"
                />
                <span className="text-sm flex-1">
                  {holiday}
                  <span className="text-muted-foreground ml-1">({count.toLocaleString()})</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Display Type Filter */}
      <div>
        <h3 className="font-medium mb-3">Display Type</h3>
        <div className="space-y-2">
          {DISPLAY_TYPE_FILTERS.map(type => {
            return (
              <label key={type} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedDisplayTypes.includes(type)}
                  onChange={() => toggleDisplayTypeFilter(type)}
                  className="rounded border-border"
                />
                <span className="text-sm flex-1">
                  {type}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Synchronized Filter */}
      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showSynchronizedOnly}
            onChange={(e) => setShowSynchronizedOnly(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm">Synchronized to Music</span>
        </label>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="h-screen flex flex-col">
        <NavBar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to load displays</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => searchDisplays()}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-screen flex flex-col">
        <NavBar />

        {/* Mobile header */}
        <div className="bg-background border-b px-4 py-3 mt-16">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">Light Displays</h1>
              <p className="text-xs text-muted-foreground">
                {isLoading ? 'Loading...' : searchQuery ? `${totalFound} results for "${searchQuery}"` : `${totalFound} displays`}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode(viewMode === 'map' ? 'list' : 'map')}
                title={viewMode === 'map' ? 'Switch to list view' : 'Switch to map view'}
              >
                {viewMode === 'map' ? <List className="h-4 w-4" /> : <Map className="h-4 w-4" />}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={getUserLocation}
                disabled={isGettingLocation}
              >
                {isGettingLocation ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Navigation className="h-4 w-4" />
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSidebar(true)}
              >
                <Menu className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Full screen map or list */}
        <div className="flex-1">
          {viewMode === 'map' ? (
            <SimpleMapView
              displays={displays}
              userLocation={userLocation}
              selectedDisplayId={selectedDisplayId}
              onDisplaySelect={handleDisplaySelect}
              selectedState={selectedState !== 'all' ? selectedState : undefined}
            />
          ) : (
            <DisplayList
              displays={paginatedDisplays}
              totalDisplays={totalFound}
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              isLoading={isLoading}
              onDisplayClick={handleDisplaySelect}
              onDisplayViewDetails={handleNavigateToDetails}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        {/* Mobile sidebar */}
        <Sheet open={showSidebar} onOpenChange={setShowSidebar}>
          <SheetContent side="left" className="w-[300px]">
            {selectedDisplay ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Display Details</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSidebar(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <DisplayDetailDrawerTabbed
                  display={selectedDisplay}
                  onClose={handleCloseDrawer}
                  className="border-0"
                />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Filters</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSidebar(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <SidebarContent />

                <div className="mt-6 border-t pt-4">
                  <div className="text-center text-muted-foreground text-sm">
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p>Tap any pin on the map to see display details</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="h-screen flex flex-col">
      <NavBar />

      {/* Desktop header */}
      <div className="bg-background border-b px-4 py-3 mt-16">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Holiday Light Displays</h1>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading displays...' : searchQuery ? `${totalFound} results for "${searchQuery}"` : `${totalFound} displays found`}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Sort dropdown */}
            <Select value={sortBy} onValueChange={(value) => setSortBy(value as any)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="trending">Trending</SelectItem>
                <SelectItem value="recent">Recently Added</SelectItem>
                <SelectItem value="featured">Featured First</SelectItem>
              </SelectContent>
            </Select>

            {/* View mode toggle - cycles through split, list, map */}
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button
                variant={viewMode === 'split' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('split')}
                className="flex items-center gap-1 h-7 px-2"
                title="Split View"
              >
                <div className="flex gap-0.5">
                  <List className="h-3 w-3" />
                  <Map className="h-3 w-3" />
                </div>
                <span className="text-xs">Split</span>
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="flex items-center gap-1 h-7 px-2"
                title="List View Only"
              >
                <List className="h-3 w-3" />
                <span className="text-xs">List</span>
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className="flex items-center gap-1 h-7 px-2"
                title="Map View Only"
              >
                <Map className="h-3 w-3" />
                <span className="text-xs">Map</span>
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={getUserLocation}
              disabled={isGettingLocation}
              className="flex items-center gap-2"
            >
              {isGettingLocation ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Finding...
                </>
              ) : (
                <>
                  <Navigation className="h-4 w-4" />
                  Find Near Me
                </>
              )}
            </Button>

            {/* Only show Filters button in map view - split/list views don't need it */}
            {viewMode === 'map' && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <ListFilter className="h-4 w-4 mr-2" />
                    Filters
                    {(selectedHolidays.length > 0 || selectedState || showFeaturedOnly || selectedDisplayTypes.length > 0 || showSynchronizedOnly || searchQuery) && (
                      <span className="ml-2 bg-primary text-primary-foreground rounded-full w-2 h-2" />
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[400px]">
                  <SidebarContent />
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {(selectedHolidays.length > 0 || (selectedState && selectedState !== 'all') || showFeaturedOnly || debouncedSearchQuery) && (
        <div className="bg-muted/30 border-b px-4 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Active filters:</span>

            {/* Search query chip */}
            {debouncedSearchQuery && (
              <div className="inline-flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm">
                <Search className="h-3 w-3" />
                {debouncedSearchQuery}
                <button
                  onClick={() => {
                    setLocalSearchQuery('');
                    setSearchParams({});
                  }}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Holiday chips */}
            {selectedHolidays.map((holiday) => (
              <div key={holiday} className="inline-flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm">
                {holiday}
                <button
                  onClick={() => toggleHolidayFilter(holiday)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}

            {/* State chip */}
            {selectedState && selectedState !== 'all' && (
              <div className="inline-flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm">
                State: {selectedState}
                <button
                  onClick={() => setSelectedState('all')}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Featured chip */}
            {showFeaturedOnly && (
              <div className="inline-flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm">
                Featured Only
                <button
                  onClick={() => setShowFeaturedOnly(false)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Display Type chips */}
            {selectedDisplayTypes.map(type => (
              <div key={type} className="inline-flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm">
                {type}
                <button
                  onClick={() => toggleDisplayTypeFilter(type)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}

            {/* Synchronized chip */}
            {showSynchronizedOnly && (
              <div className="inline-flex items-center gap-1 bg-background border rounded-full px-3 py-1 text-sm">
                Synchronized to Music
                <button
                  onClick={() => setShowSynchronizedOnly(false)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Clear all button */}
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-7 text-xs">
              Clear All
            </Button>
          </div>
        </div>
      )}

      {/* Main content - side by side */}
      <div className="flex-1 flex overflow-hidden">
        {/* SPLIT VIEW (Yelp-style): Filters, List, Map */}
        {viewMode === 'split' ? (
          <>
            {/* Left side: Filter Sidebar (300px) */}
            <div className="w-[300px] border-r overflow-y-auto bg-background">
              <SidebarContent />
            </div>

            {/* Middle: List */}
            <div className="flex-1 border-r flex flex-col overflow-hidden">
              <DisplayList
                displays={paginatedDisplays}
                totalDisplays={totalFound}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                isLoading={isLoading}
                onDisplayClick={handleDisplaySelect}
                onDisplayViewDetails={handleNavigateToDetails}
                onPageChange={setCurrentPage}
              />
            </div>

            {/* Right side: Map - shows only current page items (10) */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <SimpleMapView
                displays={paginatedDisplays}
                userLocation={userLocation}
                selectedDisplayId={selectedDisplayId}
                onDisplaySelect={handleDisplaySelect}
                selectedState={selectedState !== 'all' ? selectedState : undefined}
              />
            </div>
          </>
        ) : (
          <>
            {/* Filter sidebar - shown when in list view only */}
            {viewMode === 'list' && (
              <div className="w-[300px] border-r overflow-y-auto bg-background">
                <SidebarContent />
              </div>
            )}

            {/* Map or List - takes remaining space */}
            <div className="flex-1 flex overflow-hidden">
              {/* Display detail drawer (conditional) - only for map view when display selected */}
              {selectedDisplay && viewMode === 'map' && (
                <div className="w-1/3 border-r">
                  <DisplayDetailDrawerTabbed
                    display={selectedDisplay}
                    onClose={handleCloseDrawer}
                  />
                </div>
              )}

              {/* Main content area */}
              <div className="flex-1">
                {viewMode === 'map' ? (
                  <SimpleMapView
                    displays={mapDisplays}
                    userLocation={userLocation}
                    selectedDisplayId={selectedDisplayId}
                    onDisplaySelect={handleDisplaySelect}
                    selectedState={selectedState !== 'all' ? selectedState : undefined}
                  />
                ) : (
                  <div className="h-full flex overflow-hidden">
                    {/* List view */}
                    <div className={selectedDisplay ? "w-2/3" : "flex-1"}>
                      <DisplayList
                        displays={paginatedDisplays}
                        totalDisplays={totalFound}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        isLoading={isLoading}
                        onDisplayClick={handleDisplaySelect}
                        onDisplayViewDetails={handleNavigateToDetails}
                        onPageChange={setCurrentPage}
                      />
                    </div>

                    {/* Detail drawer in list view */}
                    {selectedDisplay && (
                      <div className="w-1/3 border-l">
                        <DisplayDetailDrawerTabbed
                          display={selectedDisplay}
                          onClose={handleCloseDrawer}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}